import { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Chip,
  Breadcrumbs,
  Link as MuiLink,
  IconButton,
  Tooltip,
  Autocomplete,
  Paper,
  Skeleton,
  Collapse,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  OpenInNew as OpenInNewIcon,
  Lock as LockIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditSectionIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  contentApi,
  type EditorialPage,
  type EditorialSection,
  type SeoMeta,
  type ContentStatus,
  type EditorialTemplate,
} from '../../services/api-client';
import { useAuth } from '../../contexts/GoogleAuthContext';
import TipTapEditor from '../../components/content/TipTapEditor';
import SeoMetaCard from '../../components/content/SeoMetaCard';
import HeroImageUpload from '../../components/content/HeroImageUpload';
import WorkflowActions from '../../components/content/WorkflowActions';
import { getTemplateConfig, type TemplateSectionDef } from '../../components/content/templateConfigs';

// =============================================================================
// Constants
// =============================================================================

const BLOG_BASE_URL = 'https://curatedby.tripsby.ai/blog';

const COMMON_TAGS = [
  'Travel',
  'Luxury',
  'Beach',
  'City Guide',
  'Food & Dining',
  'Hotels',
  'Adventure',
  'Culture',
  'Budget',
  'Family',
  'Romantic',
  'Solo Travel',
  'Wellness',
  'Nightlife',
  'Shopping',
];

// =============================================================================
// EditorialEditor — main two-column editorial content editor
// =============================================================================

export default function EditorialEditor() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const isNew = !slug || slug === 'new';

  // Permissions
  const canEdit =
    user?.role === 'admin' || user?.effective_permissions?.content?.edit === true;

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [heroImage, setHeroImage] = useState('');
  const [thumbnailImage, setThumbnailImage] = useState('');
  const [author, setAuthor] = useState('');
  const [timeToRead, setTimeToRead] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [template, setTemplate] = useState<EditorialTemplate>('article');
  const [sections, setSections] = useState<EditorialSection[]>([]);
  const [seo, setSeo] = useState<SeoMeta>({ title: '', description: '' });
  const [status, setStatus] = useState<ContentStatus>('draft');
  const [dirty, setDirty] = useState(false);
  // Track which sections are collapsed (by index). Existing content sections start collapsed.
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());

  const toggleSection = useCallback((index: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const expandAllSections = useCallback(() => {
    setExpandedSections(new Set(sections.map((_, i) => i)));
  }, [sections]);

  const collapseAllSections = useCallback(() => {
    setExpandedSections(new Set());
  }, []);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  // ---------------------------------------------------------------------------
  // Fetch existing editorial
  // ---------------------------------------------------------------------------

  const {
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['content', 'editorial', slug],
    queryFn: () => contentApi.getEditorial(slug!),
    enabled: !isNew && !!slug,
    staleTime: 30 * 1000,
    onSuccess: (data: EditorialPage) => {
      setTitle(data.title || '');
      setSubtitle(data.subtitle || '');
      setHeroImage(data.heroImage || '');
      setThumbnailImage(data.thumbnailImage || '');
      setAuthor(data.author || '');
      setTimeToRead(data.timeToRead || '');
      setTags(data.tags || []);
      setTemplate(data.template || 'article');
      setSections(data.sections || []);
      setSeo(data.seo || { title: '', description: '' });
      setStatus(data.status || 'published');
      setDirty(false);
    },
  });

  // Template config for the current template
  const templateConfig = useMemo(() => getTemplateConfig(template), [template]);

  // ---------------------------------------------------------------------------
  // Save mutation
  // ---------------------------------------------------------------------------

  const saveMutation = useMutation({
    mutationFn: () =>
      contentApi.saveEditorial({
        slug: slug || title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
        title,
        subtitle,
        heroImage,
        thumbnailImage,
        template,
        sections,
        seo,
        author,
        tags,
        timeToRead,
        generatedBy: 'human',
      }),
    onSuccess: () => {
      setDirty(false);
      setSnackbar({ open: true, message: 'Editorial saved successfully!', severity: 'success' });
      queryClient.invalidateQueries(['content', 'editorial', slug]);
      queryClient.invalidateQueries(['content', 'editorials']);
    },
    onError: (err: any) => {
      const statusCode = err?.statusCode || err?.response?.status;
      const isNotImplemented = statusCode === 404 || statusCode === 502 || statusCode === 503;
      setSnackbar({
        open: true,
        message: isNotImplemented
          ? 'Save endpoint not yet available. Content write API is under development.'
          : err?.message || 'Failed to save editorial.',
        severity: 'error',
      });
    },
  });

  // ---------------------------------------------------------------------------
  // Workflow mutations
  // ---------------------------------------------------------------------------

  const workflowMutation = useMutation({
    mutationFn: async (action: {
      type: 'review' | 'approve' | 'reject' | 'publish' | 'schedule';
      feedback?: string;
      date?: string;
    }) => {
      switch (action.type) {
        case 'review':
          return contentApi.submitForReview('editorial', slug!);
        case 'approve':
          return contentApi.approveContent('editorial', slug!, user?.email || '', true);
        case 'reject':
          return contentApi.approveContent(
            'editorial',
            slug!,
            user?.email || '',
            false,
            action.feedback
          );
        case 'publish':
          return contentApi.publishContent('editorial', slug!);
        case 'schedule':
          return contentApi.publishContent('editorial', slug!, action.date);
        default:
          throw new Error('Unknown workflow action');
      }
    },
    onSuccess: (data: any, variables) => {
      const labels: Record<string, string> = {
        review: 'Submitted for review',
        approve: 'Approved',
        reject: 'Rejected with feedback',
        publish: 'Published',
        schedule: 'Scheduled for publication',
      };
      setSnackbar({
        open: true,
        message: labels[variables.type] || 'Workflow action completed.',
        severity: 'success',
      });
      // Update local status from response
      if (data?.newStatus) {
        setStatus(data.newStatus as ContentStatus);
      }
      queryClient.invalidateQueries(['content', 'editorial', slug]);
      queryClient.invalidateQueries(['content', 'editorials']);
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message: err?.message || 'Workflow action failed.',
        severity: 'error',
      });
    },
  });

  // ---------------------------------------------------------------------------
  // Section management
  // ---------------------------------------------------------------------------

  const handleSectionContentChange = useCallback(
    (index: number, html: string) => {
      setSections((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], content: html };
        return next;
      });
      setDirty(true);
    },
    []
  );

  const handleSectionHeadingChange = useCallback(
    (index: number, heading: string) => {
      setSections((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], heading };
        return next;
      });
      setDirty(true);
    },
    []
  );

  const handleAddSection = useCallback(
    (templateSection: TemplateSectionDef, afterIndex: number) => {
      const newSection: EditorialSection = {
        type: templateSection.type,
        heading: templateSection.defaultHeading || '',
        content: '',
      };
      setSections((prev) => {
        const next = [...prev];
        next.splice(afterIndex + 1, 0, newSection);
        return next;
      });
      setDirty(true);
    },
    []
  );

  const handleRemoveSection = useCallback((index: number) => {
    setSections((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  }, []);

  // Determine which template section definition matches each section
  const sectionWithTemplateDef = useMemo(() => {
    if (!templateConfig) return sections.map((s) => ({ section: s, def: undefined }));

    // Simple mapping: match by section type against template definitions
    const defs = [...templateConfig.sections];
    const result: { section: EditorialSection; def?: TemplateSectionDef }[] = [];
    const defCounts: Record<string, number> = {};

    for (const section of sections) {
      // Find a matching template definition
      const matchingDef = defs.find((d) => {
        const key = d.label;
        const used = defCounts[key] || 0;
        if (d.type === section.type) {
          if (d.repeatable || used === 0) return true;
        }
        return false;
      });

      if (matchingDef) {
        const key = matchingDef.label;
        defCounts[key] = (defCounts[key] || 0) + 1;
      }

      result.push({ section, def: matchingDef });
    }

    return result;
  }, [sections, templateConfig]);

  // ---------------------------------------------------------------------------
  // Loading / Error states
  // ---------------------------------------------------------------------------

  if (!isNew && isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="text" width={500} height={24} sx={{ mb: 3 }} />
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="rounded" height={60} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={400} />
          </Box>
          <Box sx={{ width: 320 }}>
            <Skeleton variant="rounded" height={200} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={200} />
          </Box>
        </Box>
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load editorial. It may not exist or the Content API may be unreachable.
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/content/cms/editorials')}>
          Back to Editorials
        </Button>
      </Box>
    );
  }

  const isSaving = saveMutation.isLoading;
  const isWorkflowBusy = workflowMutation.isLoading;
  const readOnly = !canEdit;

  return (
    <Box>
      {/* Top bar: breadcrumbs + actions */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Back to editorials">
            <IconButton size="small" onClick={() => navigate('/content/cms/editorials')}>
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Breadcrumbs separator="›" sx={{ fontSize: '0.875rem' }}>
            <MuiLink
              component="button"
              variant="body2"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate('/content/cms/editorials')}
            >
              Editorials
            </MuiLink>
            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
              {isNew ? 'New Article' : title || slug}
            </Typography>
          </Breadcrumbs>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {dirty && (
            <Chip label="Unsaved changes" size="small" color="warning" variant="outlined" />
          )}

          {!isNew && (
            <Tooltip title="View on site">
              <IconButton
                size="small"
                component="a"
                href={`${BLOG_BASE_URL}/${slug}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {canEdit && (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => saveMutation.mutate()}
              disabled={isSaving || !title.trim()}
              size="small"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Info banner for read-only preview mode */}
      {!isNew && (
        <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
          <Typography variant="body2">
            <strong>Preview mode</strong> — You can view and edit content locally. Save to server is being
            connected. Changes will not persist until the write API is available.
          </Typography>
        </Alert>
      )}

      {/* Two-column layout */}
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          alignItems: 'flex-start',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* ================================================================= */}
        {/* LEFT COLUMN — Content */}
        {/* ================================================================= */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Title */}
          <TextField
            fullWidth
            variant="standard"
            placeholder="Article title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setDirty(true);
            }}
            disabled={readOnly}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.3 },
            }}
            sx={{ mb: 1 }}
          />

          {/* Subtitle */}
          <TextField
            fullWidth
            variant="standard"
            placeholder="Subtitle or tagline"
            value={subtitle}
            onChange={(e) => {
              setSubtitle(e.target.value);
              setDirty(true);
            }}
            disabled={readOnly}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: '1.1rem', color: 'text.secondary' },
            }}
            sx={{ mb: 3 }}
          />

          {/* Expand/Collapse all */}
          {sectionWithTemplateDef.length > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, gap: 1 }}>
              <Button
                size="small"
                variant="text"
                onClick={expandAllSections}
                sx={{ fontSize: '0.75rem', textTransform: 'none' }}
              >
                Expand all
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={collapseAllSections}
                sx={{ fontSize: '0.75rem', textTransform: 'none' }}
              >
                Collapse all
              </Button>
            </Box>
          )}

          {/* Sections */}
          {sectionWithTemplateDef.map(({ section, def }, idx) => {
            const isRequired = def?.required ?? false;
            const isRepeatable = def?.repeatable ?? false;
            const isExpanded = expandedSections.has(idx);

            // Show the section's actual heading first; fall back to template label, then type
            const sectionHeading = section.heading?.trim();
            const templateLabel = def?.label || section.type || `Section ${idx + 1}`;
            const displayLabel = sectionHeading || templateLabel;

            // Content preview for collapsed state — strip HTML tags, truncate
            const contentPreview = section.content
              ? section.content.replace(/<[^>]+>/g, '').trim().slice(0, 120)
              : '';

            return (
              <Paper
                key={idx}
                variant="outlined"
                sx={{ mb: 2, overflow: 'hidden' }}
              >
                {/* Section header — clickable to expand/collapse */}
                <Box
                  onClick={() => toggleSection(idx)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    backgroundColor: isExpanded ? 'grey.50' : 'background.paper',
                    borderBottom: isExpanded ? '1px solid' : 'none',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'grey.50' },
                    transition: 'background-color 0.15s',
                  }}
                >
                  {isExpanded ? (
                    <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  ) : (
                    <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  )}

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                        noWrap
                      >
                        {displayLabel}
                      </Typography>
                      {/* Show template label as secondary badge when heading is used as primary */}
                      {sectionHeading && sectionHeading !== templateLabel && (
                        <Chip
                          label={templateLabel}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 18, ml: 0.5 }}
                        />
                      )}
                      {isRequired && (
                        <Tooltip title="Required section">
                          <LockIcon
                            sx={{ fontSize: 14, color: 'text.disabled', verticalAlign: 'middle' }}
                          />
                        </Tooltip>
                      )}
                    </Box>

                    {/* Content preview when collapsed */}
                    {!isExpanded && contentPreview && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ display: 'block', mt: 0.25, maxWidth: '100%' }}
                      >
                        {contentPreview}{contentPreview.length >= 120 ? '...' : ''}
                      </Typography>
                    )}
                  </Box>

                  {/* Section action buttons — stop propagation so they don't toggle collapse */}
                  <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', gap: 0.5 }}>
                    {!isExpanded && canEdit && (
                      <Tooltip title="Edit section">
                        <IconButton
                          size="small"
                          onClick={() => toggleSection(idx)}
                        >
                          <EditSectionIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    {isRepeatable && canEdit && (
                      <Tooltip title={`Add another ${templateLabel}`}>
                        <IconButton
                          size="small"
                          onClick={() => def && handleAddSection(def, idx)}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    {!isRequired && canEdit && (
                      <Tooltip title="Remove section">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveSection(idx)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                {/* Collapsible section body */}
                <Collapse in={isExpanded} unmountOnExit={false}>
                  {/* Section heading field */}
                  {section.heading !== undefined && (
                    <Box sx={{ px: 2, pt: 1.5 }}>
                      <TextField
                        fullWidth
                        variant="standard"
                        placeholder="Section heading"
                        value={section.heading}
                        onChange={(e) => handleSectionHeadingChange(idx, e.target.value)}
                        disabled={readOnly}
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '1.2rem', fontWeight: 600 },
                        }}
                      />
                    </Box>
                  )}

                  {/* Section content — TipTap editor */}
                  {section.type === 'richtext' || section.type === 'featured_grid' ? (
                    <Box sx={{ px: 2, pb: 2, pt: 1 }}>
                      <TipTapEditor
                        content={section.content || ''}
                        onChange={(html) => handleSectionContentChange(idx, html)}
                        placeholder={def?.placeholder || 'Start writing...'}
                        minHeight={180}
                        readOnly={readOnly}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ px: 2, pb: 2, pt: 1 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={section.content || ''}
                        onChange={(e) => handleSectionContentChange(idx, e.target.value)}
                        disabled={readOnly}
                        placeholder={def?.placeholder || 'Enter content...'}
                      />
                    </Box>
                  )}
                </Collapse>
              </Paper>
            );
          })}

          {/* Add section button (if template has repeatable sections) */}
          {canEdit && templateConfig && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {templateConfig.sections
                .filter((s) => s.repeatable || !sections.some((sec) => sec.type === s.type))
                .filter((s) => !s.required || !sections.some((sec) => sec.type === s.type))
                .map((tplSection) => (
                  <Button
                    key={tplSection.label}
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddSection(tplSection, sections.length - 1)}
                  >
                    Add {tplSection.label}
                  </Button>
                ))}
            </Box>
          )}

          {/* Empty state */}
          {sections.length === 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: 'divider',
              }}
            >
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                No content sections yet. Add sections to start building your article.
              </Typography>
              {canEdit && templateConfig && (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {templateConfig.sections
                    .filter((s) => s.required)
                    .map((tplSection) => (
                      <Button
                        key={tplSection.label}
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddSection(tplSection, -1)}
                      >
                        Add {tplSection.label}
                      </Button>
                    ))}
                </Box>
              )}
            </Paper>
          )}
        </Box>

        {/* ================================================================= */}
        {/* RIGHT COLUMN — Metadata sidebar */}
        {/* ================================================================= */}
        <Box
          sx={{
            width: { xs: '100%', md: 320 },
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {/* Workflow actions */}
          {!isNew && (
            <WorkflowActions
              status={status}
              onSubmitForReview={() => workflowMutation.mutate({ type: 'review' })}
              onApprove={() => workflowMutation.mutate({ type: 'approve' })}
              onReject={(feedback) => workflowMutation.mutate({ type: 'reject', feedback })}
              onPublish={() => workflowMutation.mutate({ type: 'publish' })}
              onSchedule={(date) => workflowMutation.mutate({ type: 'schedule', date })}
              disabled={isWorkflowBusy || isSaving}
              canEdit={canEdit}
            />
          )}

          {/* Article metadata */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Article Details
              </Typography>

              <TextField
                fullWidth
                size="small"
                label="Author"
                value={author}
                onChange={(e) => {
                  setAuthor(e.target.value);
                  setDirty(true);
                }}
                disabled={readOnly}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                size="small"
                label="Time to Read"
                value={timeToRead}
                onChange={(e) => {
                  setTimeToRead(e.target.value);
                  setDirty(true);
                }}
                disabled={readOnly}
                placeholder="e.g. 5 min read"
                sx={{ mb: 2 }}
              />

              {/* Slug (read-only for existing) */}
              {!isNew && (
                <TextField
                  fullWidth
                  size="small"
                  label="Slug"
                  value={slug}
                  disabled
                  sx={{ mb: 2 }}
                  helperText="URL slug cannot be changed"
                />
              )}

              {/* Template chip */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Template:
                </Typography>
                <Chip
                  label={templateConfig?.label || template}
                  size="small"
                  variant="outlined"
                  sx={{ textTransform: 'capitalize' }}
                />
              </Box>

              {/* Tags */}
              <Autocomplete
                multiple
                freeSolo
                size="small"
                options={COMMON_TAGS}
                value={tags}
                onChange={(_, newValue) => {
                  setTags(newValue);
                  setDirty(true);
                }}
                disabled={readOnly}
                renderInput={(params) => (
                  <TextField {...params} label="Tags" placeholder="Add tags..." />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((tag, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={tag}
                      label={tag}
                      size="small"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  ))
                }
              />
            </CardContent>
          </Card>

          {/* Images */}
          <HeroImageUpload
            heroImage={heroImage}
            thumbnailImage={thumbnailImage}
            onHeroChange={(url) => {
              setHeroImage(url);
              setDirty(true);
            }}
            onThumbnailChange={(url) => {
              setThumbnailImage(url);
              setDirty(true);
            }}
            disabled={readOnly}
          />

          {/* SEO */}
          <SeoMetaCard
            seo={seo}
            onChange={(newSeo) => {
              setSeo(newSeo);
              setDirty(true);
            }}
            disabled={readOnly}
          />
        </Box>
      </Box>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
